/**
 * Templates AI Edit Feature Types
 *
 * Type definitions specific to the template builder feature.
 * Extends the shared AG-UI types from ai-chat-core.
 */

import type { AgUiChatState } from '@features/ai-chat-core';
import type {
  DraftSectionResponse,
  DraftTemplateResponse,
  TemplateBuildConfirmRequest,
  TemplateBuildStatusResponse,
  TemplateBuildUploadResponse,
} from '@store/generated/templates';

// ============================================================================
// Type Declaration Merging - Extend RootState
// ============================================================================

declare module '@store/types' {
  interface DynamicFeatureState {
    'templatesAiEdit/ui'?: TemplateBuildState;
    'templatesAiEdit/streaming'?: unknown; // RTK Query streaming cache
  }
}

// ============================================================================
// Re-export Generated Types with Aliases
// ============================================================================

export type { DraftSectionResponse, DraftTemplateResponse, TemplateBuildConfirmRequest, TemplateBuildStatusResponse, TemplateBuildUploadResponse };

// ============================================================================
// Draft Template Types
// ============================================================================

/**
 * Draft section from the build workflow
 * Mirrors DraftSectionResponse but with camelCase for frontend use
 */
export interface DraftSection {
  id: string;
  title: string;
  order: number;
  content: string;
  exampleOutput: string | null;
}

/**
 * Draft template being built through conversation
 */
export interface DraftTemplate {
  title: string;
  description: string | null;
  sections: DraftSection[];
}

/**
 * Whether a completed save forked instead of updating in place.
 *
 * The server publishes a build task by forking from its source template unless
 * the caller already owns that template — and it forks again on every save,
 * ignoring the template it created last time. So a session seeded from a public
 * template spawns a fresh copy per save. Detect it by the save writing somewhere
 * other than where the session started (GDN-3099).
 */
export function shouldHandOffToFork(params: {
  isAdminMode: boolean;
  sourceTemplateId: string | null;
  savedTemplateId: string | null;
}): boolean {
  const { isAdminMode, sourceTemplateId, savedTemplateId } = params;

  // Admin saves target another user's template and navigate away afterwards;
  // the provider build endpoint can't reseed on their behalf.
  if (isAdminMode) return false;
  if (!sourceTemplateId || !savedTemplateId) return false;

  return savedTemplateId !== sourceTemplateId;
}

/**
 * Convert API response to frontend DraftTemplate
 */
export function toDraftTemplate(response: DraftTemplateResponse): DraftTemplate {
  return {
    title: response.title,
    description: response.description ?? null,
    sections: response.sections.map(s => ({
      id: s.id,
      title: s.title,
      order: s.order,
      content: s.content,
      exampleOutput: s.example_output ?? null,
    })),
  };
}

/**
 * The shape `draftTemplateSignature` needs. Satisfied by both the in-flight
 * draft and a published revision from the templates API, so the two can be
 * compared directly.
 */
export interface SignableTemplate {
  title: string;
  description?: string | null;
  sections?: Array<{ id: string; title: string; order?: number; content: string }>;
}

/**
 * Stable fingerprint of a template's publishable content.
 *
 * Used to tell "already saved" from "edited since the last save". Only the
 * fields a publish actually writes are included — example output is a
 * regenerated preview, not something the provider is saving.
 */
export function draftTemplateSignature(template: SignableTemplate): string {
  return JSON.stringify({
    title: template.title,
    description: template.description ?? null,
    sections: (template.sections ?? [])
      .map((s, index) => ({
        id: s.id,
        title: s.title,
        order: s.order ?? index,
        content: s.content,
      }))
      .sort((a, b) => a.order - b.order),
  });
}

/**
 * Convert API section response to frontend DraftSection
 */
export function toDraftSection(response: DraftSectionResponse): DraftSection {
  return {
    id: response.id,
    title: response.title,
    order: response.order,
    content: response.content,
    exampleOutput: response.example_output ?? null,
  };
}

// ============================================================================
// Build State Types
// ============================================================================

/**
 * Build status values
 */
export type BuildStatus =
  | 'idle'
  | 'uploading'
  | 'processing'
  | 'structure_ready'
  | 'ready_for_review'
  | 'completed'
  | 'failed';

/**
 * Template build state - extends base AG-UI chat state
 */
export interface TemplateBuildState extends AgUiChatState {
  // Build session
  taskId: string | null;
  buildStatus: BuildStatus;
  progressMessage: string | null;

  // Document metadata (for PDFs, available early in processing)
  documentTitle: string | null;
  documentSummary: string | null;

  // Draft template state (from polling or ACTIVITY_SNAPSHOT)
  draftTemplate: DraftTemplate | null;

  // Section selection for context
  selectedSectionId: string | null;

  // Template this session was seeded from. When a save writes to a different
  // template than this, the server forked (the source wasn't ours to edit).
  sourceTemplateId: string | null;

  // After save completes
  createdTemplateId: string | null;

  // Fingerprint of the draft as of the last successful publish. Null until the
  // session has saved once. Compared against the live draft to answer "is what
  // I'm looking at already saved?" (GDN-3099)
  savedDraftSignature: string | null;

  // Flag to trigger status refetch after chat completes
  needsStatusRefetch: boolean;
}

// ============================================================================
// Request Payload Types
// ============================================================================

/**
 * Template build chat message request
 */
export interface TemplateBuildChatRequest {
  taskId: string;
  threadId: string | null;
  runId: string;
  messages?: Array<{
    id: string;
    role: 'user' | 'assistant';
    content: string;
  }>;
  resume?: {
    interruptId: string;
    payload:
      | { confirmed: boolean }
      | { selected_ids: string[] }
      | { text: string }
      | Record<string, { selected_ids?: string[]; confirmed?: boolean }>;
  };
  selected_section_id?: string;
}

/**
 * Union type for all request payloads
 */
export type TemplateBuildRequestPayload = TemplateBuildChatRequest;

// ============================================================================
// Streaming Data Types
// ============================================================================

/**
 * Streaming cache data for template build chat
 */
export interface TemplateBuildStreamData {
  isActive: boolean;
  isDone: boolean;
}

// ============================================================================
// Upload Types
// ============================================================================

/**
 * Upload mode - PDF file or pasted text
 */
export type UploadMode = 'pdf' | 'text';

/**
 * Upload state
 */
export interface UploadState {
  mode: UploadMode;
  file: File | null;
  text: string;
  isUploading: boolean;
  error: string | null;
}

